import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import NotFound from "@/pages/NotFound";
import { ArrowLeft, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPost = () => {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [article, setArticle] = useState(null);
  const [status, setStatus] = useState("loading"); // loading|ok|missing|error

  useEffect(() => {
    axios
      .get(`${API}/blog/articles/${slug}`)
      .then((res) => {
        setArticle(res.data);
        setStatus("ok");
      })
      .catch((err) => {
        if (err?.response?.status === 404) setStatus("missing");
        else setStatus("error");
      });
  }, [slug]);

  if (status === "missing") return <NotFound />;
  if (status === "loading") return (
    <div className="container-x pt-40 pb-40 text-white/50">{t("blog.loading")}</div>
  );
  if (status === "error" || !article) return (
    <div className="container-x pt-40 pb-40 text-red-400">Error loading article.</div>
  );

  const title = lang === "ar" ? article.title_ar || article.title_en : article.title_en || article.title_ar;
  const body = lang === "ar" ? article.body_ar || article.body_en : article.body_en || article.body_ar;
  const excerpt = lang === "ar" ? article.excerpt_ar || article.excerpt_en : article.excerpt_en || article.excerpt_ar;

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB", { year: "numeric", month: "long", day: "numeric" });
    } catch { return ""; }
  };

  return (
    <div data-testid="blog-post-page">
      <section className="relative pt-32 md:pt-40">
        {article.cover_image ? (
          <div className="relative aspect-[16/9] md:aspect-[21/9] overflow-hidden">
            <img src={article.cover_image} alt={title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/40 to-transparent" />
          </div>
        ) : (
          <div className="h-40" />
        )}
        <div className="container-x -mt-24 md:-mt-32 relative max-w-4xl">
          <Link to="/blog" className="inline-flex items-center gap-2 text-white/80 hover:text-[#E6A95B] mb-6 text-sm font-medium">
            <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("blog.back")}
          </Link>
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.24em] text-[#E6A95B] mb-4">
            <Calendar className="w-3.5 h-3.5" />
            <span>{formatDate(article.created_at)}</span>
            <span className="text-white/40">·</span>
            <span className="text-white/70">{article.author}</span>
          </div>
          <h1 className="display-h1">{title}</h1>
          {excerpt && <p className="mt-6 text-white/70 text-xl max-w-3xl leading-relaxed">{excerpt}</p>}
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container-x max-w-3xl">
          <article className="prose prose-invert prose-lg max-w-none text-white/85">
            {(body || "").split(/\n\n+/).map((para, i) => (
              <p key={i} className="text-lg md:text-xl leading-[1.9] mb-6 whitespace-pre-wrap">{para}</p>
            ))}
          </article>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
